import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { getStripe, STRIPE_APPEARANCE } from '../services/stripeClient';

// ── Inline card entry (BILL-002) ────────────────────────────────────────────
//
// SECURITY, WHICH IS THE WHOLE POINT OF DOING IT THIS WAY
// PaymentElement renders Stripe-hosted iframes. The card number, expiry and CVC
// live inside Stripe's origin; this component cannot read them, and neither can
// anything else on the page — not our error boundary, not our analytics, not a
// browser extension reading our DOM. What comes back is a PaymentMethod id.
//
// That is not a nice-to-have. It is what keeps this application in PCI SAQ A
// (the lightest scope, for merchants who fully outsource card capture to a
// compliant provider). Replacing PaymentElement with our own <input> fields —
// even ones that only forward the value straight to Stripe — moves us to SAQ
// A-EP and brings the whole application into audit scope.
//
// The consequence for error handling: `error` objects from confirmSetup carry
// a decline code and a message, never card data, so they are safe to log. Do
// NOT widen that by logging the Elements state or anything read from the DOM.
//
// 3DS / SCA: confirmSetup with redirect: 'if_required' runs the bank's
// challenge in a modal over this page and resolves afterwards. The user never
// leaves the upgrade flow, which is the requirement that makes a redirect-based
// approach unacceptable here.

/**
 * The fields themselves. Separate from the <Elements> provider because
 * useStripe/useElements only work beneath it.
 *
 * Exposes confirm() to the parent rather than owning a submit button: the
 * dialog's single "Confirm and pay" button has to do card confirmation AND the
 * plan change in one action, so the parent has to drive.
 */
const CardFields = forwardRef(({ onReady, onValidityChange }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);

  useImperativeHandle(ref, () => ({
    /**
     * Tokenise and confirm the card against the SetupIntent.
     *
     * Resolves { paymentMethodId } on success, or { error } with a
     * human-readable message. Never throws, so the caller can keep the user in
     * the flow rather than unwinding to a generic error dialog.
     */
    async confirm() {
      if (!stripe || !elements) {
        return { error: 'The payment form is still loading. Give it a moment and try again.' };
      }

      // Surface field-level problems before going near the network.
      const submitResult = await elements.submit();
      if (submitResult.error) {
        return { error: submitResult.error.message || 'Check the card details and try again.' };
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        // 'if_required' keeps 3DS in a modal on this page. The alternative
        // redirects to the bank and back, which would destroy the upgrade flow
        // this ticket exists to preserve.
        redirect: 'if_required',
      });

      if (error) {
        return { error: error.message || 'That card could not be saved. Try another card.' };
      }
      if (!setupIntent?.payment_method) {
        return { error: 'The card was not saved. Please try again.' };
      }
      return { paymentMethodId: setupIntent.payment_method };
    },
    isReady: () => ready,
  }), [stripe, elements, ready]);

  return (
    <Box>
      {!ready && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={18} sx={{ color: '#1C7C73' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#8A8F85' }}>
            Loading secure card form…
          </Typography>
        </Box>
      )}
      <PaymentElement
        options={{
          layout: 'tabs',
          // Billing details are collected by our own address form, so asking
          // again here would duplicate it. Stripe still collects postal code
          // where the card network requires it for authorisation.
          fields: { billingDetails: { address: { country: 'auto', postalCode: 'auto' } } },
        }}
        onReady={() => { setReady(true); onReady?.(); }}
        onChange={(e) => onValidityChange?.(e.complete)}
      />
    </Box>
  );
});

CardFields.displayName = 'CardFields';

/**
 * <Elements> provider plus the fields.
 *
 * `clientSecret` comes from a SetupIntent created server-side. Elements is
 * keyed on it so that a NEW SetupIntent (e.g. after the user retries following
 * a decline) remounts the form cleanly instead of confirming against a stale
 * intent — a mistake that produces a confusing "this intent has already been
 * confirmed" error rather than the retry the user asked for.
 */
const InlineCardForm = forwardRef(({ clientSecret, onReady, onValidityChange }, ref) => {
  const stripePromise = getStripe();

  if (!stripePromise) {
    // Should be unreachable: callers check isInlineCardEntryAvailable() and use
    // the hosted redirect instead. Rendering something honest beats rendering
    // an empty box if that check is ever missed.
    return (
      <Typography role="alert" sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#DC2626' }}>
        Card entry is unavailable right now. Please add a card from Settings.
      </Typography>
    );
  }

  if (!clientSecret) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
        <CircularProgress size={18} sx={{ color: '#1C7C73' }} />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#8A8F85' }}>
          Preparing secure card form…
        </Typography>
      </Box>
    );
  }

  return (
    <Elements
      key={clientSecret}
      stripe={stripePromise}
      options={{ clientSecret, appearance: STRIPE_APPEARANCE }}
    >
      <CardFields ref={ref} onReady={onReady} onValidityChange={onValidityChange} />
    </Elements>
  );
});

InlineCardForm.displayName = 'InlineCardForm';

export default InlineCardForm;
